import { useCallback, useEffect, useRef, useState } from 'react';

const SOUND_STORAGE_KEY = 'yard-gate-dock-alert-sound';
const BELL_BASE_FREQUENCY_HZ = 660;
const BELL_ATTACK_S = 0.005;
const BELL_PARTIALS = [
  { ratio: 0.5, gain: 0.25, decay: 1.4 },
  { ratio: 1, gain: 0.3, decay: 1.2 },
  { ratio: 2, gain: 0.2, decay: 0.9 },
  { ratio: 2.76, gain: 0.12, decay: 0.7 },
  { ratio: 4, gain: 0.08, decay: 0.5 },
];

type AudioContextConstructor = typeof AudioContext;

const resolveAudioContextConstructor = (): AudioContextConstructor | null => {
  const scope = window as unknown as {
    AudioContext?: AudioContextConstructor;
    webkitAudioContext?: AudioContextConstructor;
  };
  return scope.AudioContext ?? scope.webkitAudioContext ?? null;
};

const readSoundEnabled = (): boolean => {
  try {
    return window.localStorage.getItem(SOUND_STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
};

const writeSoundEnabled = (enabled: boolean): void => {
  try {
    window.localStorage.setItem(SOUND_STORAGE_KEY, enabled ? 'on' : 'off');
  } catch {
    return;
  }
};

export const useDockAlertSound = () => {
  const [soundEnabled, setSoundEnabled] = useState(readSoundEnabled);
  const audioContextRef = useRef<AudioContext | null>(null);

  const getAudioContext = useCallback((): AudioContext | null => {
    if (audioContextRef.current) return audioContextRef.current;

    const AudioContextClass = resolveAudioContextConstructor();
    if (!AudioContextClass) return null;

    audioContextRef.current = new AudioContextClass();
    return audioContextRef.current;
  }, []);

  useEffect(() => {
    const unlock = () => {
      const context = getAudioContext();
      if (context && context.state === 'suspended') {
        void context.resume();
      }
    };

    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [getAudioContext]);

  const playDockAlert = useCallback(() => {
    const context = getAudioContext();
    if (!context) return;

    if (context.state === 'suspended') {
      void context.resume();
    }

    const start = context.currentTime + 0.02;
    BELL_PARTIALS.forEach(({ ratio, gain: peakGain, decay }) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = BELL_BASE_FREQUENCY_HZ * ratio;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(peakGain, start + BELL_ATTACK_S);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + decay);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(start);
      oscillator.stop(start + decay + 0.05);
    });
  }, [getAudioContext]);

  const toggleSound = useCallback(() => {
    const next = !soundEnabled;
    writeSoundEnabled(next);
    setSoundEnabled(next);
    if (next) playDockAlert();
  }, [soundEnabled, playDockAlert]);

  return { soundEnabled, toggleSound, playDockAlert };
};
